import {
	Component,
	computed,
	effect,
	inject,
	input,
	output,
	signal,
} from '@angular/core';
import { Router } from '@angular/router';
import { Capacitor } from '@capacitor/core';
import { ViewActivityDialogComponent } from '@context/frontend/activity';
import { AlertService } from '@context/frontend/alert';
import { DialogService, RelativeTimePipe } from '@context/frontend/common';
import { ContentService } from '@context/frontend/content';
import { FileService } from '@context/frontend/native/file';
import { getUaDevice, IsDevicePipe } from '@context/frontend/native/platform';
import { StorageService } from '@context/frontend/storage';
import {
	AcknowledgeDialogComponent,
	DetailMenuDialogComponent,
	DetailMenuDropdownComponent,
} from '@context/frontend/ui/components';
import { AuthService } from '@context/frontend/user';
import { ViewerService } from '@context/frontend/viewer';
import {
	Content,
	DetailMenuItem,
	ViewAccess,
} from '@context/shared/types/common';
import {
	ContentMedia,
	downloadBlob,
	isMediaImage,
} from '@context/shared/types/media';
import { hasInheritedRolePermission, User } from '@context/shared/types/user';
import { TranslocoModule } from '@jsverse/transloco';
import { TuiButton, TuiDropdown, TuiHint, TuiIcon } from '@taiga-ui/core';
import { TuiBadge } from '@taiga-ui/kit';
import { PolymorpheusComponent } from '@taiga-ui/polymorpheus';
import { DocumentReference } from 'firebase/firestore';
import { MediaEditDialogComponent } from '../../dialogs';
import { MediaService } from '../../services';
import { MediaThumbnailComponent } from '../media-thumbnail/media-thumbnail.component';

@Component({
	imports: [
		TranslocoModule,
		RelativeTimePipe,
		MediaThumbnailComponent,
		TuiButton,
		TuiIcon,
		DetailMenuDropdownComponent,
		TuiDropdown,
		IsDevicePipe,
		TuiHint,
		TuiBadge,
	],
	selector: 'ctx-media-row',
	templateUrl: 'media-row.component.html',
	styleUrl: 'media-row.component.scss',
	host: {
		'[attr.device]': 'device',
		'[attr.archived]': 'isArchived()',
	},
})
export class MediaRowComponent {
	readonly device = getUaDevice();

	readonly media = input.required<ContentMedia>();

	readonly mediaRef = input.required<DocumentReference<ContentMedia>>();

	readonly parent = input<Content | null>(null);
	readonly parentId = computed(
		// have to check the parents parent of the media to get the crop
		() => this.parent()?.id || this.mediaRef().parent?.parent?.id,
	);

	readonly showParent = input(false);

	readonly access = input<ViewAccess>('edit');

	readonly update = output<void>();

	readonly preview = output<void>();

	private readonly dialog = inject(DialogService);
	private readonly storageService = inject(StorageService);
	private readonly contentService = inject(ContentService);
	private readonly mediaService = inject(MediaService);
	private readonly alert = inject(AlertService);
	private readonly authService = inject(AuthService);
	private readonly viewer = inject(ViewerService);
	private readonly router = inject(Router);
	private readonly fileService = inject(FileService);

	readonly isArchived = computed(() => {
		const now = new Date().valueOf();
		const media = this.media();
		return media.archivedAt && media.archivedAt.toDate().valueOf() < now;
	});

	menuOpen = false;

	readonly menuItems = signal<DetailMenuItem[]>([]);

	readonly defaultMenuItems: DetailMenuItem[] = [
		{
			text: 'action.download',
			iconStart: '@tui.download',
			handler: () => this.onDownload(),
		},
		{
			text: 'action.view-history',
			iconStart: '@tui.history',
			handler: () => this.onViewHistory(),
		},
	];

	readonly activeMenuItems: DetailMenuItem[] = [
		{
			text: 'content.edit-details',
			iconStart: '@tui.pen-line',
			handler: () => this.onEdit(),
		},
		{
			text: 'content.action.archive',
			iconStart: '@tui.archive',
			handler: () => this.onArchive(),
		},
	];

	readonly mediaDetailMenu = () =>
		this.dialog.open(
			new PolymorpheusComponent(DetailMenuDialogComponent),
			DetailMenuDialogComponent.Options({
				label: 'media.manage-media-item',
				data: { menuItems: this.menuItems },
			}),
		);

	readonly mediaActivityDialog = () =>
		this.dialog.open(
			new PolymorpheusComponent(ViewActivityDialogComponent),
			ViewActivityDialogComponent.Options({
				data: { entityRef: this.mediaRef() },
			}),
		);

	readonly acknowledgeDialog = (
		label: string,
		description: string,
		acknowledge: string,
	) =>
		this.dialog.open(
			new PolymorpheusComponent(AcknowledgeDialogComponent),
			AcknowledgeDialogComponent.Options({
				label,
				data: {
					description,
					acknowledge,
					translationParams: { media: this.media().name },
				},
			}),
		);

	readonly mediaArchiveDialog = () =>
		this.acknowledgeDialog(
			'media.archive-header',
			'media.archive-description',
			'media.archive-acknowledge',
		);

	readonly mediaUnarchiveDialog = () =>
		this.acknowledgeDialog(
			'media.unarchive-header',
			'media.unarchive-description',
			'media.unarchive-acknowledge',
		);

	readonly mediaDeleteDialog = () =>
		this.acknowledgeDialog(
			'media.delete-header',
			'media.delete-description',
			'media.delete-acknowledge',
		);

	readonly mediaDestroyDialog = () =>
		this.acknowledgeDialog(
			'media.destroy-header',
			'media.destroy-description',
			'media.destroy-acknowledge',
		);

	readonly mediaEditDetailDialog = () =>
		this.dialog.open(
			new PolymorpheusComponent(MediaEditDialogComponent),
			MediaEditDialogComponent.Options({
				data: { media: this.media(), mediaRef: this.mediaRef() },
			}),
		);

	constructor() {
		effect(() => {
			if (this.access() !== 'view') {
				this.setupMenuItems();
			}
		});
	}

	setupMenuItems() {
		// some actions are unavailable when media is attached to a log
		this.menuItems.set([...this.defaultMenuItems]);

		const media = this.media();
		if (!media.log) {
			if (this.isArchived()) {
				this.menuItems.update((items) => [
					...items,
					{
						text: 'content.restore-from-archive',
						iconStart: '@tui.archive-restore',
						handler: () => this.onUnarchive(),
					},
				]);

				// we can only delete the media if the user is an admin or greater
				const currentUser = this.authService.user as User;
				if (hasInheritedRolePermission(currentUser.role, 'admin')) {
					this.menuItems.update((items) => [
						...items,
						{
							text: 'media.delete-media',
							iconStart: '@tui.trash',
							handler: () => this.onDelete(),
						},
					]);
				}

				if (hasInheritedRolePermission(currentUser.role, 'super')) {
					this.menuItems.update((items) => [
						...items,
						{
							text: 'media.destroy-media',
							iconStart: '@tui.bomb',
							handler: () => this.onDestroy(),
						},
					]);
				}
			} else {
				this.menuItems.update((items) => [
					...items,
					...this.activeMenuItems,
				]);
			}
		}

		// only can apply the thumbnail if we are in the context of the parent
		if (this.parent()) {
			if (!this.isArchived && isMediaImage(media.type)) {
				if (media.setAsThumbnail) {
					this.menuItems.update((items) => [
						...items,
						{
							iconStart: '@tui.image-minus',
							text: 'media.action.remove-as-thumbnail',
							handler: () => this.onRemoveAsThumbnail(),
						},
					]);
				} else {
					this.menuItems.update((items) => [
						...items,
						{
							iconStart: '@tui.image-plus',
							text: 'media.action.use-as-thumbnail',
							handler: () => this.onUseAsThumbnail(),
						},
					]);
				}
			}
		} else if (this.parentId()) {
			// if not in the context of the direct parent, we can view the crop to go to it
			this.menuItems.update((items) => [
				...items,
				{
					iconEnd: '@tui.chevron-right',
					iconStart: '@tui.sprout',
					text: 'media.view-crop',
					handler: () => this.onGoToContent(),
				},
			]);
		}
	}

	readonly onMenuOpen = () => this.mediaDetailMenu().subscribe();

	onPreviewLog = () => this.preview.emit();

	readonly onGoToContent = () =>
		this.router.navigate(['/home/file/', this.parentId()!]);

	readonly onUseAsThumbnail = () =>
		this.contentService
			.setContentMediaDerivedThumbnail({
				content: this.parent()!,
				media: this.media(),
				derived: 'media',
				ref: this.mediaRef(),
			})
			.then(() => {
				this.alert.open('generic-success').subscribe();
				this.menuOpen = false;
				this.update.emit();
			})
			.catch((error) => {
				console.error(error);
				this.alert.open('generic-error').subscribe();
				throw new Error(
					'There was an issue setting the content media thumbnail',
				);
			});

	readonly onRemoveAsThumbnail = () =>
		this.contentService
			.checkAndRemoveContentMediaThumbnail({
				content: this.parent()!,
				mediaRef: this.mediaRef(),
			})
			.then(() => {
				this.alert.open('generic-success').subscribe();
				this.menuOpen = false;
				this.update.emit();
			})
			.catch((error) => {
				console.error(error);
				this.alert.open('generic-error').subscribe();
				throw new Error(
					'There was an issue setting the content media thumbnail',
				);
			});

	async onDownload() {
		this.menuOpen = false;
		const { name, storagePath } = this.media();

		this.alert.open('file-downloading').subscribe();

		try {
			const ref = this.storageService.getReference(storagePath);
			if (Capacitor.isNativePlatform()) {
				const url = await this.storageService.getDownloadUrl(ref);
				await this.fileService.downloadFile(url, name);
			} else {
				const blob = await this.storageService.getBlob(ref);
				downloadBlob({ name, blob });
			}

			this.alert.open('file-downloaded').subscribe();
		} catch (error) {
			console.error(error);
			this.alert.open('file-downloaded-error').subscribe();
			throw Error('There was an issue downloading the media');
		}
	}

	readonly onEdit = () =>
		this.mediaEditDetailDialog().subscribe({
			next: (data) => {
				if (data) this.update.emit();
			},
		});

	readonly onViewHistory = () => this.mediaActivityDialog().subscribe();

	readonly onView = () => this.viewer.preview(this.media());

	readonly onArchive = () =>
		this.mediaArchiveDialog().subscribe({
			next: (data) => {
				if (data) {
					this.mediaService
						.archive(this.parent()!, this.mediaRef())
						.then(() => {
							this.alert.open('generic-success').subscribe();
							this.update.emit();
						})
						.catch((error) => {
							this.alert.open('generic-error').subscribe();
							console.error(error);
							throw new Error(
								'There was an issue taking action on the media',
							);
						});
				}
			},
		});

	readonly onUnarchive = () =>
		this.mediaUnarchiveDialog().subscribe({
			next: (data) => {
				if (data) {
					this.mediaService
						.unarchive(this.mediaRef())
						.then(() => {
							this.alert.open('generic-success').subscribe();
							this.update.emit();
						})
						.catch((error) => {
							this.alert.open('generic-error').subscribe();
							console.error(error);
							throw new Error('Could not unarchive the media');
						});
				}
			},
		});

	readonly onDelete = () =>
		this.mediaDeleteDialog().subscribe({
			next: (data) => {
				if (data) {
					this.mediaService
						.delete(this.parent()!, this.mediaRef())
						.then(() => {
							this.alert.open('generic-success').subscribe();
							this.update.emit();
						})
						.catch((error) => {
							this.alert.open('generic-error').subscribe();
							console.error(error);
							throw new Error(
								'There was an issue deleting on the media',
							);
						});
				}
			},
		});

	/**
	 * @param force `true` to bypass the parent archived check
	 */
	readonly onDestroy = () =>
		this.mediaDestroyDialog().subscribe({
			next: (data) => {
				if (data) {
					const parent = this.parent();
					if (!parent) return;

					this.mediaService
						.delete(parent, this.mediaRef(), { hard: true })
						.then(() => {
							this.alert.open('generic-success').subscribe();
							this.update.emit();
						})
						.catch((error) => {
							this.alert.open('generic-error').subscribe();
							console.error(error);
							throw new Error(
								'There was an issue destroying on the media',
							);
						});
				}
			},
		});
}
